import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { logout } from '../../lib/api';
import { appSelectors, setLoggedOut } from '../../redux/appSlice';
import LoginForm from '../LoginForm';
import LostPasswordForm from '../LostPasswordForm';
import Modal from '../Modal';
import RegisterForm from '../RegisterForm';

export const LoginBar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const username: string = useAppSelector(appSelectors.username);
  const loggedIn: boolean = useAppSelector(appSelectors.isLoggedIn);
  const handleLogoutClick = useCallback(async () => {
    const response: boolean = await logout();
    if (response) {
      dispatch(setLoggedOut());
      setSearchParams(
        (currentSearchParams: URLSearchParams): URLSearchParams => {
          currentSearchParams.delete('account');
          currentSearchParams.delete('back');
          return currentSearchParams;
        }
      );
    }
  }, [dispatch, setSearchParams]);

  const handleLoginClick = () => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.set('account', 'login');
      return currentSearchParams;
    });
  };

  const handleRegisterClick = (back = true) => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.set('account', 'register');
      if (back) currentSearchParams.set('back', 'login');
      return currentSearchParams;
    });
  };

  const handleLostPasswordClick = () => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.set('account', 'new-password');
      return currentSearchParams;
    });
  };

  const closeModals = () => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.delete('account');
      currentSearchParams.delete('back');
      return currentSearchParams;
    });
  };

  const accountParam = searchParams.get('account');
  const backParam = searchParams.get('back');
  const showLoginForm = accountParam === 'login';
  const showRegisterForm = accountParam === 'register';
  const showLostPasswordForm = accountParam === 'new-password';

  return (
    <div className="login-bar">
      <div className="login-bar-inner">
        {loggedIn && (
          <ul>
            <li>Hello {`${username}`}</li>
            <li className="collection-link">
              <Link to={'/user/collections'}>Your collections</Link>
            </li>
            <li>
              <button className="logout-button" onClick={handleLogoutClick}>
                Logout
              </button>
            </li>
          </ul>
        )}

        {!loggedIn && (
          <div className="login-wrapper">
            <button className="login-button" onClick={handleLoginClick}>
              Login
            </button>
            <button
              className="login-button"
              onClick={() => handleRegisterClick(false)}
            >
              Create account
            </button>
            <Modal
              isOpened={showLoginForm}
              showBack={true}
              title="Login"
              onClose={closeModals}
            >
              <LoginForm
                onRegisterClick={handleRegisterClick}
                onLostPasswordClick={handleLostPasswordClick}
              />
            </Modal>
            <Modal
              isOpened={showRegisterForm}
              showBack={true}
              backType={backParam && backParam === 'login' ? 'back' : 'close'}
              title="Request a user login"
              onClose={() => {
                closeModals();
                if (backParam && backParam === 'login') {
                  handleLoginClick();
                }
              }}
            >
              <RegisterForm />
            </Modal>
            <Modal
              isOpened={showLostPasswordForm}
              showBack={true}
              backType="back"
              title="Request new password"
              onClose={handleLoginClick}
            >
              <LostPasswordForm />
            </Modal>
          </div>
        )}
      </div>
    </div>
  );
};
